import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState, Reducer } from 'react';
import moment from 'moment';
import * as am4charts from '@amcharts/amcharts4/charts';
import { Color } from '@amcharts/amcharts4/core';

import { useTranslation } from '@lib/useTypedTranslation';
import { TTypedTFunction } from '@lib/useTypedTranslation';
import { getSelectedDateDeviceEventsList } from '../../../../../services/core/devicePerformance';
import { DeviceEvent, DeviceEventName } from '../../../../../services/core/eventsTypes';
import { DeviceInfoContext } from '../../index';
import { useWorldRequest } from '../../../../../lib/useWorldRequest';
import { useFeatureTogglesContext } from '../../../../../context/featureToggles';
import { useUserSettingsContext } from '../../../../../context/userSettings';
import { DeviceNetworkChartEventsFilterState, deviceEventFilterGroupNames } from '../performance/events/deviceEventsTypes';
import { EditEvents, EditEventsAction, editEventsReducer, getActiveEventFilters, useEditEventsDispatch } from '../performance/events/edit-events';
import { networkChartName, NetworkChartName } from '../performance/charts/lib/chartNames';
import { filterDeviceEvents, getDeviceEventFilterGroups, getDeviceEvents } from '../performance/events/deviceEvents';
import { EventsShown } from '../performance/events-shown';
import { StackedColumnsDateChart } from '@components/chart/stackedColumnsDateChart';
import { drawXAxisLabelsFromDateRange } from '@components/chart/lib';
import { roundToNearest5Minutes } from '@lib/timeUtils';
import { getInitialFilters } from '../performance/index';
import { WifiSignalChartData } from './deviceWifi';
import { useWorldAction } from '@lib/useWorldAction';
import { exportDeviceWifiSignalStrength } from '../../../../../services/core/deviceWifi';
import { ExportButton } from '@components/controls/exportButton';

import '../network/edit-events.css';
import '../performance/events/deviceEvents.css';
import '../performance/events/edit-events.css';
import '../performance/events-shown.css';
import './wifiSignalChart.css';
import { ResetZoomButton } from '../performance/charts/lib/resetZoomButton';
import { useChartZoom } from '@components/chart/useChartZoom';

interface IProps {
  selectedDate: number,
  barColours: string[],
  data: WifiSignalChartData[],
  noDataAvailable: boolean,
  loading: boolean
}

interface IRequestParams {
  deviceId: string,
  to: number,
  events?: DeviceEventName[]
}

type EventsWithRoundedTime = {
  event: DeviceEvent,
  date: string,
  tooltipDate: number
};

function getRequestParams(deviceId: string, dates: { to: number }): IRequestParams {
  const { to } = dates;
  return { deviceId, to };
}

export function getLevelToDescriptionMapping(t: TTypedTFunction): { [key: string]: string } {
  return {
    '0': t('VERY_POOR', { ns: 'deviceWifi' }),
    '1': t('POOR', { ns: 'deviceWifi' }),
    '2': t('MODERATE', { ns: 'deviceWifi' }),
    '3': t('GOOD', { ns: 'deviceWifi' }),
    '4': t('VERY_GOOD', { ns: 'deviceWifi' })
  };
}

export function getTooltipAdapter({ t }: { t: TTypedTFunction }) {
  return (_: any, target: any) => {
    let dataItem = target.getSeriesDataItem(
      target.chart.series.getIndex(0) as any,
      target.toAxisPosition((target.chart as any).cursor.xPosition),
      true
    );
    const data = dataItem.dataContext as WifiSignalChartData;
    const date = moment.utc(data.local).format('HH:mm:ss');

    if (data.radioOff) {
      return `[font-weight: 600]${date}[/]
      ${t('WIFI_RADIO_OFF', { ns: 'deviceWifi' })}`;
    }

    if (data.disconnected) {
      return `[font-weight: 600]${date}[/]
      ${t('NO_WIFI_SERVICE', { ns: 'deviceWifi' })}`;
    }

    if (data.noDataAvailable) {
      return `[font-weight: 600]${date}[/]
      ${t('NO_DATA_AVAILABLE', { ns: 'translation' })}`;
    }

    const levelToDescriptionMapping = getLevelToDescriptionMapping(t);
    const signal = `${t('SIGNAL', { ns: 'deviceWifi' })}: ${levelToDescriptionMapping[data.level] || '-'}`;
    const rssi = `${t('RSSI', { ns: 'deviceWifi' })}: ${data.original?.rssi !== null && data.original?.rssi !== undefined ? `${data.original.rssi} dBm` : '-'}`;
    const ssid = `${t('SSID', { ns: 'deviceWifi' })}: ${data.original?.ssid || '-'}`;
    const bssid = `${t('BSSID', { ns: 'deviceWifi' })}: ${data.original?.bssid || '-'}`;
    const frequency = `${t('FREQUENCY', { ns: 'deviceWifi' })}: ${data.original?.frequency ? `${data.original.frequency} GHz` : '-'}`;

    return `[font-weight: 600]${date}[/]
    ${signal}
    ${rssi}
    ${ssid}
    ${bssid}
    ${frequency}
    `;
  };
}

const getEventsWithRoundedTime = (deviceEvents: DeviceEvent[]): EventsWithRoundedTime[] => {
  return deviceEvents.map((event: DeviceEvent) => {
    const local = roundToNearest5Minutes(event.local);
    return {
      event,
      date: moment.utc(local).format('HH:mm'),
      tooltipDate: event.local
    };
  });
};

export const getAllSeriesData = (wifiData: WifiSignalChartData[], deviceEvents: DeviceEvent[]): WifiSignalChartData[] => {
  const dataWithEvents: WifiSignalChartData[] = wifiData.slice();
  const eventsWithRoundedTime = getEventsWithRoundedTime(deviceEvents);

  const createDateLookup = dataWithEvents.reduce((acc: any, data: any, index: number) => {
    acc[data.dateFormatted] = { index };
    return acc;
  }, {});

  eventsWithRoundedTime.forEach((event: EventsWithRoundedTime) => {
    const dateEntry = createDateLookup[event.date];
    if (dateEntry) {
      const data = dataWithEvents[dateEntry.index];
      const events = data.events ? data.events.concat(event.event) : [event.event];

      dataWithEvents[dateEntry.index] = { ...data, events, tooltipDate: event.tooltipDate };
    }
  });

  return dataWithEvents;
};

export function WifiSignalChart(props: IProps) {
  const {
    selectedDate,
    barColours,
    data,
    noDataAvailable,
    loading
  } = props;

  const featureToggles = useFeatureTogglesContext();
  const userSettings = useUserSettingsContext();
  const { platformType, id: deviceId, timeZone } = useContext(DeviceInfoContext);
  const { isZoomed, handleChartMount, handleResetZoom } = useChartZoom();

  const exportWifiSignalStrengthList = useWorldAction(exportDeviceWifiSignalStrength);
  const onExportClicked = async (): Promise<string> => {
    return exportWifiSignalStrengthList({ deviceId, to: dates.to });
  };
  const { t } = useTranslation(['deviceWifi', 'translation']);

  const [allEvents, setAllEvents] = useState([]);
  const [deviceNetworkEvents, setDeviceNetworkEvents] = useState([]);
  const [chartDataLoading, setChartDataLoading] = useState(false);

  const initialChartFilters = getInitialFilters(userSettings, featureToggles, platformType, 'chart', true);
  const [chartEventFilters, dispatchChartEventFilters] = useReducer<Reducer<DeviceNetworkChartEventsFilterState, EditEventsAction<NetworkChartName>>>(
    editEventsReducer,
    initialChartFilters
  );

  const eventFilterDeviceNetwork = chartEventFilters.deviceWifi;

  const deviceEventNames = useMemo(() => {
    return Object.values(getDeviceEvents(platformType, featureToggles)).map(event => event.name);
  }, [platformType, featureToggles]);

  const dates = useMemo(() => ({
    from: moment.utc(selectedDate).startOf('day').valueOf(),
    to: moment.utc(selectedDate).endOf('day').valueOf()
  }), [selectedDate]);

  function setLoadingTrue() {
    setChartDataLoading(true);
  }

  const {
    handleUpdate, handleApply, handleCancel
  } = useEditEventsDispatch(
    dispatchChartEventFilters, networkChartName.deviceWifi, setLoadingTrue
  );

  const onEventsFetched = useCallback((events: DeviceEvent[]) => {
    setAllEvents(events);
  }, []);

  const eventsDataFetcher = useCallback(() => {
    if (deviceId && !noDataAvailable) {
      const params = getRequestParams(deviceId, dates);
      return getSelectedDateDeviceEventsList({ ...params, events: deviceEventNames });
    }
  }, [deviceId, dates, deviceEventNames, noDataAvailable]);

  useWorldRequest(eventsDataFetcher, { initialLoading: false, initialData: [], onSuccess: onEventsFetched });

  const activeEventsDeviceNetwork = useMemo(() => getActiveEventFilters(eventFilterDeviceNetwork.appliedEventFilters), [eventFilterDeviceNetwork.appliedEventFilters]);

  useEffect(() => {
    const [networkEvents] = filterDeviceEvents(allEvents, activeEventsDeviceNetwork);
    setDeviceNetworkEvents(networkEvents);
  }, [allEvents, activeEventsDeviceNetwork]);

  return (
    <div className={`core-device_wifi-signal${!noDataAvailable && !chartDataLoading ? ' chart-ready' : ''}`}>
      <div className="wifi-chart-header">
        <div className="wifi-chart-title">{t('WIFI_SIGNAL_LEVEL', { ns: 'deviceWifi' })}</div>
        <div className="wifi-chart-edit-events">
          <EventsShown eventFilters={eventFilterDeviceNetwork.appliedEventFilters} />
          <div className="wifi-chart-controls">
            <ExportButton
              exportFunction={onExportClicked}
              filename={`DeviceWifiSignalStrength_.${moment.utc(selectedDate).format(`LLLL 'YY`)}.csv`}
              disabled={loading || noDataAvailable}
              className="wifi-chart-export-button"
            />
            <EditEvents
              subheader={t('WIFI_SIGNAL_LEVEL', { ns: 'deviceWifi' })}
              modalSubHeader={t('EDIT_EVENTS_MODAL_HEADER', { ns: 'deviceWifi' })}
              eventGroupNames={deviceEventFilterGroupNames}
              eventFilterGroups={getDeviceEventFilterGroups(platformType, featureToggles)}
              eventFilterState={eventFilterDeviceNetwork.currentEventFilters}
              handleUpdate={handleUpdate}
              handleApply={handleApply}
              handleCancel={handleCancel}
            />
            <ResetZoomButton resetZoom={handleResetZoom} zoomedIn={isZoomed} isHidden={true} />
          </div>
        </div>
      </div>
      <StackedColumnsDateChart<any>
        data={getAllSeriesData(data, deviceNetworkEvents)}
        onlyShowCurrentSeriesInTooltip={true}
        series={[
          ...barColours.map(/* istanbul ignore next - acceptance tested due to jsdom limitations */(colour, i) => {
            const seriesKey = i === 0 ? 'disconnected' : (i - 1).toString();

            return {
              linePattern: seriesKey === 'disconnected' ? {
                width: 5,
                height: 5,
                strokeWidth: 1,
                stroke: new Color({ r: 0, g: 0, b: 0 }),
                rotation: -45
              } : undefined,
              visible: true,
              dataKey: seriesKey,
              description: seriesKey,
              colour
            };
          }),
          {
            visible: true,
            dataKey: 'networkEvents',
            description: '',
            colour: '#FFFFFF'
          }
        ]}
        title=''
        cypressId='wifiSignalStrength'
        days={1}
        drawXAxis={(_, xAxis) => {
          drawXAxisLabelsFromDateRange(xAxis as am4charts.DateAxis, dates);
          (xAxis as am4charts.DateAxis).baseInterval = { timeUnit: 'minute', count: 5 };
          (xAxis as am4charts.DateAxis).min = dates.from;
          (xAxis as am4charts.DateAxis).max = dates.to;
          xAxis.renderer.labels.template.location = 0.001 as any;
          xAxis.title.text = `[font-style: italic]${t('DEVICE_LOCAL_TIME', { ns: 'translation', timeZone: timeZone?.name ? ` (${timeZone.name})` : '' })}[/]`;
        }}
        tooltipDateKey='date'
        hasLegend={false}
        columnWidth={70}
        drawYAxis={(_, yAxis) => {
          const levelToDescriptionMapping = getLevelToDescriptionMapping(t);

          yAxis.min = 0;
          yAxis.max = barColours.length - 1;
          yAxis.strictMinMax = true;

          yAxis.title.dx = -15;

          yAxis.renderer.grid.template.disabled = true;

          yAxis.renderer.labels.template.disabled = true;
          yAxis.renderer.labels.template.horizontalCenter = 'middle';
          yAxis.renderer.ticks.template.disabled = true;
          yAxis.renderer.ticks.template.hidden = true;

          yAxis.axisRanges.clear();

          new Array(barColours.length - 1).fill(0).forEach((_, barLevel) => {
            const range = yAxis.axisRanges.create();
            range.label.text = levelToDescriptionMapping[barLevel];
            range.value = barLevel + 0.5;
            range.label.disabled = false;
          });
        }}
        hasNoSelectionOverlay={false}
        eventOverlay={true}
        dateAxisEndDuration={{ amount: '5', unit: 'minute' }}
        useAxisTooltip={true}
        getAxisTooltipAdapter={() => getTooltipAdapter({ t })}
        className='core-device_wifi-signal-chart-container'
        dragToZoom
        eventTickLength={140}
        onMount={handleChartMount}
      />
    </div>
  );
}
